import { useMemo, useRef } from 'react'
import { useSessionStorage } from 'react-use'

import { ColumnConditionValue, TableColumnListValue, TypographySelectValue } from '@makyoapp/makyo-common'
import { ColorVariant, CornerRadius, getTypographyTokenStyle, HoverEffectVariant, Prompt } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ComponentSettingKey, SpacingInputUnit } from '@/shared/builder'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { getSafely } from '@/shared/safely'

import { useCompPagination } from '../../hooks/useCompPagination'

export interface CompPromptWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
}

const BACKGROUND_COLOR_TYPES = {
  adaptive: 'adaptive',
  solid: 'solid',
  gradient: 'gradient',
} as const

function generateBackgroundColor(
  backgroundColorType: string,
  currentParentBg: ColorVariant,
  backgroundColorSolid: ColorVariant,
  backgroundColorStartGradient: ColorVariant,
  backgroundColorEndGradient: ColorVariant,
  backgroundColorGradientDirection: string
): ColorVariant {
  const gradientColor =
    backgroundColorGradientDirection === 'to-left' ? backgroundColorEndGradient : backgroundColorStartGradient

  const colorMap = {
    [BACKGROUND_COLOR_TYPES.adaptive]: currentParentBg,
    [BACKGROUND_COLOR_TYPES.solid]: backgroundColorSolid,
    [BACKGROUND_COLOR_TYPES.gradient]: gradientColor,
  }

  return colorMap[backgroundColorType as keyof typeof colorMap] || currentParentBg
}

export function CompPromptWrapperCore({
  infos: { settings, currentParentBg, component },
  action: { simulateAction },
}: CompPromptWrapperCoreProps) {
  const { isPhonePortrait } = useBreakpoint()
  const { isDevelopmentMode, openComponentSettings, setActiveComponentAccordion } = useAppBuilderContext()
  const { setAccordions } = useBuilderAccordionContext()
  const { tablesMap } = useDbClientContext()

  const compId = component._id

  const dataSource = settings[ComponentSettingKey.PromptDataSource]
  const titleColumn = settings[ComponentSettingKey.PromptDataTitle] as string | undefined
  const descriptionColumn = settings[ComponentSettingKey.PromptDataDescription] as string | undefined

  const limitSuggestions = settings[ComponentSettingKey.PromptDesignNumberDefaultItems] || 10

  // pagination state
  const { pagination, setPagination } = useCompPagination({
    compId,
    defaultPerPage: 500,
  })

  // data filter settings
  const filterSetting = settings[ComponentSettingKey.ListDataFilter] as ColumnConditionValue | undefined
  const sortBy = settings[ComponentSettingKey.ListDataSortBy] as string
  const sortType = settings[ComponentSettingKey.ListDataSortType] as string

  // user filter state
  const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

  // Get table from dataSource
  const table = dataSource ? tablesMap.get(dataSource) : undefined

  // Get rows from table
  const [{ rows }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination,
    filterSetting,
    sortSetting: { sortType, sortBy },
    search: '',
    userFilter,
  })

  // Store previous suggestions to prevent shuffle during loading
  const previousSuggestionsRef = useRef<Array<{ title: string; description: string }>>([])
  const previousRowsHashRef = useRef<string>('')

  // Map rows to suggestions format with stable keys
  const suggestions = useMemo(() => {
    // If loading, return previous suggestions to prevent shuffle
    if (isLoading) {
      return previousSuggestionsRef.current
    }

    if (!table || !titleColumn || !descriptionColumn || !rows.length) {
      const empty: Array<{ title: string; description: string }> = []
      previousSuggestionsRef.current = empty
      previousRowsHashRef.current = ''
      return empty
    }

    // Create a stable hash from row IDs and their key values to detect actual changes
    const rowsHash = rows
      .map((row) => `${row._id}:${row[titleColumn]?.toString() || ''}:${row[descriptionColumn]?.toString() || ''}`)
      .join('|')

    // If rows haven't actually changed, return previous suggestions to prevent shuffle
    if (rowsHash === previousRowsHashRef.current && previousSuggestionsRef.current.length > 0) {
      return previousSuggestionsRef.current
    }

    const mapped = rows
      .map((row) => {
        const title = row[titleColumn]?.toString() || ''
        const description = row[descriptionColumn]?.toString() || ''

        if (!title && !description) {
          return null
        }

        return {
          title,
          description,
        }
      })
      .filter((item): item is { title: string; description: string } => item !== null)

    // Update refs only when data is stable and actually changed
    previousSuggestionsRef.current = mapped
    previousRowsHashRef.current = rowsHash

    return mapped
  }, [table, titleColumn, descriptionColumn, rows, isLoading])

  const placeholder = settings[ComponentSettingKey.PromptDataPlaceholder] || 'What would you like to create today?'

  // ========================= styles =========================
  const width = settings[ComponentSettingKey.PromptDesignWidth] || '100%'
  const alignment = settings[ComponentSettingKey.PromptDesignHAlign] || 'parent'

  // background color
  const backgroundColor = settings[ComponentSettingKey.PromptDesignBackgroundColor] || 'adaptive'
  const backgroundColorSolid = settings[ComponentSettingKey.PromptDesignBackgroundColorSolid] || 'primary-40'
  const backgroundColorStartGradient =
    settings[ComponentSettingKey.PromptDesignBackgroundColorStartGradient] || 'primary-40'
  const backgroundColorEndGradient =
    settings[ComponentSettingKey.PromptDesignBackgroundColorEndGradient] || 'primary-80'
  const backgroundColorGradientDirection =
    settings[ComponentSettingKey.PromptDesignBackgroundColorGradientDirection] || 'to-right'

  const buttonColor = settings[ComponentSettingKey.PromptDesignButtonColorVariant] || 'primary-40'
  const margin = settings[ComponentSettingKey.PromptDesignMargin] || {
    top: {
      value: 0,
      unit: SpacingInputUnit.Pixel,
    },
    bottom: {
      value: 0,
      unit: SpacingInputUnit.Pixel,
    },
    left: {
      value: 0,
      unit: SpacingInputUnit.Pixel,
    },
    right: {
      value: 0,
      unit: SpacingInputUnit.Pixel,
    },
  }
  const suggestionPadding = settings[ComponentSettingKey.PromptDesignSuggestionPadding]
  const shuffleButtonPadding = settings[ComponentSettingKey.PromptDesignShuffleButtonPadding]

  // ========================= behavior =========================
  const acceptedFiles = settings[ComponentSettingKey.PromptBehaviorAcceptedFiles]
  const maxSize = settings[ComponentSettingKey.PromptBehaviorMaxSize]
  const maxFiles = settings[ComponentSettingKey.PromptBehaviorMaxFiles]
  const colorSelection = settings[ComponentSettingKey.PromptBehaviorColorSelection]
  const fileSelection = settings[ComponentSettingKey.PromptBehaviorFileSelection]
  const fontSelection = settings[ComponentSettingKey.PromptBehaviorFontSelection]

  const dividerColor = settings[ComponentSettingKey.PromptDesignDividerColor]
  const placeholderColor = settings[ComponentSettingKey.PromptDesignPlaceholderColor]
  const dividerColorSolid = settings[ComponentSettingKey.PromptDesignDividerColorSolid]
  const placeholderColorSolid = settings[ComponentSettingKey.PromptDesignPlaceholderColorSolid]
  const dividerColorStartGradient = settings[ComponentSettingKey.PromptDesignDividerColorStartGradient]
  const placeholderColorStartGradient = settings[ComponentSettingKey.PromptDesignPlaceholderColorStartGradient]
  const dividerColorEndGradient = settings[ComponentSettingKey.PromptDesignDividerColorEndGradient]
  const placeholderColorEndGradient = settings[ComponentSettingKey.PromptDesignPlaceholderColorEndGradient]
  const dividerColorGradientDirection = settings[ComponentSettingKey.PromptDesignDividerColorGradientDirection]
  const placeholderColorGradientDirection = settings[ComponentSettingKey.PromptDesignPlaceholderColorGradientDirection]

  const buttonColorVariant = settings[ComponentSettingKey.PromptDesignButtonColorVariant] || 'adaptive'
  const buttonColorVariantSolid = settings[ComponentSettingKey.PromptDesignButtonColorVariantSolid] || 'primary-40'
  const buttonColorVariantStartGradient =
    settings[ComponentSettingKey.PromptDesignButtonColorVariantStartGradient] || 'primary-40'
  const buttonColorVariantEndGradient =
    settings[ComponentSettingKey.PromptDesignButtonColorVariantEndGradient] || 'primary-80'
  const buttonColorVariantGradientDirection =
    settings[ComponentSettingKey.PromptDesignButtonColorVariantGradientDirection] || 'to-right'

  const chipsAlignment = settings[ComponentSettingKey.PromptDesignChipsAlignment] || 'center'
  const chipsColor = settings[ComponentSettingKey.PromptDesignChipColor] || 'adaptive'
  const chipsColorSolid = settings[ComponentSettingKey.PromptDesignChipColorSolid] || 'primary-40'
  const chipsColorStartGradient = settings[ComponentSettingKey.PromptDesignChipColorStartGradient] || 'primary-40'
  const chipsColorEndGradient = settings[ComponentSettingKey.PromptDesignChipColorEndGradient] || 'primary-80'
  const chipsColorGradientDirection = settings[ComponentSettingKey.PromptDesignChipColorGradientDirection] || 'to-right'

  const chipsGap = settings[ComponentSettingKey.PromptDesignChipsGap]?.[0] || 0

  const typoSettings = settings[ComponentSettingKey.PromptDesignTypography]
  const placeholderTypography = getSafely(typoSettings, [ComponentSettingKey.PromptDesignPlaceholderTypography], {
    typography: 'Title Small',
  } as TypographySelectValue)
  const suggestionTypography = getSafely(typoSettings, [ComponentSettingKey.PromptDesignSuggestionTypography], {
    typography: 'Body Small',
  } as TypographySelectValue)

  const borderThickness = settings[ComponentSettingKey.PromptDesignBorderThickness]
  const borderColor = settings[ComponentSettingKey.PromptDesignBorderColor] || 'adaptive'
  const borderColorSolid = settings[ComponentSettingKey.PromptDesignBorderColorSolid] || 'primary-40'

  const elevation = settings[ComponentSettingKey.PromptDesignElevation] || false

  const cornerRadius = settings[ComponentSettingKey.PromptDesignCornerRadius] as CornerRadius
  const buttonCornerRadius = settings[ComponentSettingKey.PromptDesignButtonCornerRadius] as CornerRadius

  const hoverEffect = settings[ComponentSettingKey.PromptDesignHoverEffectVariant] as HoverEffectVariant

  const BYTE_MULTIPLIERS = {
    b: 1,
    bytes: 1,
    kb: 1024,
    kilobytes: 1024,
    mb: 1024 * 1024,
    megabytes: 1024 * 1024,
    gb: 1024 * 1024 * 1024,
    gigabytes: 1024 * 1024 * 1024,
  } as const

  const maxSizeInByte = (() => {
    if (!maxSize?.value) return undefined

    const unit = maxSize.unit?.toLowerCase()
    const multiplier = BYTE_MULTIPLIERS[unit as keyof typeof BYTE_MULTIPLIERS]

    return multiplier ? maxSize.value * multiplier : maxSize.value
  })()

  const actionOnButtonClick = settings[ComponentSettingKey.PromptActionOnButtonClick]

  const actionOnImproveAiClick = settings[ComponentSettingKey.PromptActionOnImproveAiClick]

  const handleImproveAiClick = () => {
    if (component?._id)
      setAccordions('action', component._id, `action-${ComponentSettingKey.PromptActionOnImproveAiClick}`)
    setActiveComponentAccordion(`action-${ComponentSettingKey.PromptActionOnImproveAiClick}`)
    openComponentSettings(component, ComponentPanelTab.Action)

    if (isDevelopmentMode) return

    if (actionOnImproveAiClick) {
      simulateAction({
        actions: actionOnImproveAiClick,
      })
    }
  }

  const handleSubmit = () => {
    if (component?._id)
      setAccordions('action', component._id, `action-${ComponentSettingKey.PromptActionOnButtonClick}`)
    setActiveComponentAccordion(`action-${ComponentSettingKey.PromptActionOnButtonClick}`)
    openComponentSettings(component, ComponentPanelTab.Action)

    if (isDevelopmentMode) return

    if (actionOnButtonClick) {
      simulateAction({
        actions: actionOnButtonClick,
      })
    }
  }

  const bgColor = generateBackgroundColor(
    backgroundColor,
    currentParentBg as ColorVariant,
    backgroundColorSolid,
    backgroundColorStartGradient,
    backgroundColorEndGradient,
    backgroundColorGradientDirection
  )

  const promptComponent = (
    <Prompt
      backgroundColor={bgColor}
      bgType={backgroundColor ? 'color' : 'parent'}
      placeholder={placeholder}
      width={width}
      alignment={alignment}
      accept={acceptedFiles}
      maxFileSize={maxSizeInByte}
      maxFiles={maxFiles}
      selectColor={colorSelection}
      fileSelection={fileSelection}
      fontSelection={fontSelection}
      buttonColor={buttonColor}
      margin={margin}
      dividerColorType={dividerColor}
      placeholderColorType={placeholderColor}
      dividerColorSolid={dividerColorSolid}
      placeholderColorSolid={placeholderColorSolid}
      dividerColorStartGradient={dividerColorStartGradient}
      placeholderColorStartGradient={placeholderColorStartGradient}
      dividerColorEndGradient={dividerColorEndGradient}
      placeholderColorEndGradient={placeholderColorEndGradient}
      dividerColorGradientDirection={dividerColorGradientDirection}
      placeholderColorGradientDirection={placeholderColorGradientDirection}
      suggestionsAlignment={chipsAlignment}
      suggestionButtonColorType={chipsColor}
      suggestionButtonColorSolid={chipsColorSolid}
      suggestionButtonStartGradient={chipsColorStartGradient}
      suggestionButtonEndGradient={chipsColorEndGradient}
      suggestionButtonGradientDirection={chipsColorGradientDirection}
      screenSize={isPhonePortrait ? 'mobile-portrait' : 'desktop'}
      placeholderTypography={placeholderTypography}
      borderThickness={borderThickness}
      borderColorType={borderColor}
      borderColorSolid={borderColorSolid}
      // button color
      submitButtonColorType={buttonColorVariant}
      submitButtonColorSolid={buttonColorVariantSolid}
      submitButtonColorStartGradient={buttonColorVariantStartGradient}
      submitButtonColorEndGradient={buttonColorVariantEndGradient}
      submitButtonColorGradientDirection={buttonColorVariantGradientDirection}
      // background color
      backgroundColorType={backgroundColor}
      backgroundColorSolid={backgroundColorSolid}
      backgroundColorStartGradient={backgroundColorStartGradient}
      backgroundColorEndGradient={backgroundColorEndGradient}
      backgroundColorGradientDirection={backgroundColorGradientDirection}
      elevation={elevation}
      cornerRadius={cornerRadius}
      buttonCornerRadius={buttonCornerRadius}
      onSubmit={handleSubmit}
      onImprove={handleImproveAiClick}
      devMode={isDevelopmentMode}
      onDevModeComponentClick={() => {
        openComponentSettings(component, ComponentPanelTab.Data)
      }}
      onDevModeButtonClick={handleSubmit}
      suggestions={suggestions}
      limitSuggestions={limitSuggestions || 4}
      hoverEffect={hoverEffect}
      hoverIntensity='md'
      suggestionsGap={chipsGap}
      suggestionTypography={suggestionTypography}
      suggestionPadding={suggestionPadding}
      shuffleButtonPadding={shuffleButtonPadding}
    />
  )

  return promptComponent
}
